/**
 * Meta Pixel — thin, safe wrapper around window.fbq.
 *
 * The pixel loader is injected in `public/index.html`. We call fbq only
 * when it's a function (guards against ad-blockers, opt-outs and the
 * initial render tick before the pixel finishes loading). Every helper
 * also idempotently guards against duplicate events fired from React 18
 * StrictMode double-invocations by using a per-session dedupe key on the
 * high-value events (Purchase, InitiateCheckout).
 *
 * Standard event mapping
 * ----------------------
 *   viewContent(item)     → Meta "ViewContent"   — customer viewed a product page / opened a modal
 *   addToCart(cartItem)   → Meta "AddToCart"     — customer added an item to the basket
 *   initiateCheckout(cart)→ Meta "InitiateCheckout" — customer opened the checkout page
 *   purchase(order)       → Meta "Purchase"      — order paid (or booked as COD)
 *   search(query)         → Meta "Search"        — customer typed in the menu search
 *
 * Ads Manager can then optimise campaigns for AddToCart / Purchase
 * conversions instead of just PageViews.
 */
const isFn = () => typeof window !== 'undefined' && typeof window.fbq === 'function';

// Dedupe key store for high-value events. React 18 StrictMode in
// development fires effects twice; without dedupe we'd double-report
// Purchase events.
const seen = new Set();
const guard = (key) => {
  if (!key) return false;
  if (seen.has(key)) return true;
  seen.add(key);
  // Also mark in sessionStorage so a hard refresh doesn't double-fire.
  try {
    const stored = JSON.parse(sessionStorage.getItem('__fbq_seen') || '[]');
    if (stored.includes(key)) return true;
    stored.push(key);
    sessionStorage.setItem('__fbq_seen', JSON.stringify(stored.slice(-100)));
  } catch (_e) { /* sessionStorage disabled — carry on */ }
  return false;
};

const gbp = (n) => Math.max(0, Math.round(Number(n || 0) * 100) / 100);

export const track = (event, params) => {
  if (!isFn()) return;
  try {
    window.fbq('track', event, params || {});
  } catch (_e) { /* swallow — pixel errors must never break UX */ }
};

export const viewContent = (item) => {
  if (!item) return;
  track('ViewContent', {
    content_ids: [item.id || item.item_id],
    content_name: item.name,
    content_type: item.item_type === 'bundle' ? 'product_group' : 'product',
    content_category: item.category || undefined,
    currency: 'GBP',
    value: gbp(item.price),
  });
};

export const addToCart = (item) => {
  if (!item) return;
  track('AddToCart', {
    content_ids: [item.item_id || item.id],
    content_name: item.name,
    content_type: item.item_type === 'bundle' ? 'product_group' : 'product',
    contents: [
      {
        id: item.item_id || item.id,
        quantity: item.quantity || 1,
        item_price: gbp(item.price),
      },
    ],
    currency: 'GBP',
    value: gbp((item.price || 0) * (item.quantity || 1)),
  });
};

export const initiateCheckout = (cartItems, total) => {
  const items = cartItems || [];
  track('InitiateCheckout', {
    content_ids: items.map((i) => i.item_id).filter(Boolean),
    contents: items.map((i) => ({
      id: i.item_id,
      quantity: i.quantity || 1,
      item_price: gbp(i.price),
    })),
    num_items: items.reduce((n, i) => n + (i.quantity || 1), 0),
    currency: 'GBP',
    value: gbp(total),
  });
};

export const purchase = (order) => {
  if (!order || !order.id) return;
  const key = `Purchase:${order.id}`;
  if (guard(key)) return; // never double-fire per session
  const items = order.items || [];
  track('Purchase', {
    content_ids: items.map((i) => i.item_id).filter(Boolean),
    contents: items.map((i) => ({
      id: i.item_id,
      quantity: i.quantity || 1,
      item_price: gbp(i.price),
    })),
    num_items: items.reduce((n, i) => n + (i.quantity || 1), 0),
    currency: 'GBP',
    value: gbp(order.total_amount),
    order_id: order.id,
  });
};

// Simple throttle so we don't fire a Search event on every keystroke.
let searchTimer = null;
export const search = (query) => {
  if (!query || query.trim().length < 3) return;
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    track('Search', { search_string: query.trim() });
  }, 700);
};
